/**
 * Premium Excel (.xlsx) export — FinMaple Budget Planner
 *
 * Etsy-quality single-sheet dashboard with FinMaple brand identity.
 * Soft pastel section colors, card-style metrics, hidden gridlines.
 *
 *   Sheet 1: Dashboard — metric cards + all budget sections
 *   Sheet 2: 12-Month Projection
 *
 * Uses exceljs (dynamic import) to stay out of the main bundle.
 */

import { toMonthly } from '../../utils/frequency';
import { calculateHealthScore } from '../../utils/financialHealth';

/* ─── FinMaple brand palette (ARGB) ──────────────────────────────── */
const P = {
  emerald:       'FF00695C',
  navy:          'FF1F3A5F',
  maple:         'FFD32F2F',
  gold:          'FFF9A825',
  teal:          'FF00838F',

  emeraldPastel: 'FFD4EDDA',
  navyPastel:    'FFD6E0F0',
  maplePastel:   'FFF8D7DA',
  goldPastel:    'FFFFF3CD',
  tealPastel:    'FFD4F0F4',

  emeraldTint:   'FFF0FAF5',
  navyTint:      'FFF5F7FC',
  mapleTint:     'FFFFFAFA',
  goldTint:      'FFFFFDF5',
  tealTint:      'FFF0FAFB',

  white:         'FFFFFFFF',
  border:        'FFE8E8E8',
  borderLight:   'FFF0F0F0',
  textDark:      'FF2D3436',
  textMuted:     'FF8E9AAF',
};

const currFmt = '"$"#,##0.00';

function bg(argb) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

function fmtCAD(n) {
  const s = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (n < 0 ? '-$' : '$') + s;
}

/** Fill every cell in a range — ensures merged-cell backgrounds render */
function fillRange(ws, r1, c1, r2, c2, color) {
  for (let r = r1; r <= r2; r++)
    for (let c = c1; c <= c2; c++)
      ws.getCell(r, c).fill = bg(color);
}

/* ─── i18n labels ────────────────────────────────────────────────── */
const L_ALL = {
  es: {
    dashboard: 'Mi Presupuesto', subtitle: 'Presupuesto Personal',
    income: 'INGRESOS', fixedExpenses: 'GASTOS FIJOS',
    variableExpenses: 'GASTOS VARIABLES', debtPlan: 'DEUDAS',
    projection: 'Proyecci\u00f3n 12 Meses', summary: 'RESUMEN',
    monthlyIncome: 'Ingreso Mensual', monthlyExpenses: 'Gastos Mensuales',
    available: 'Disponible', healthScore: 'Salud Financiera',
    savingsRate: 'Tasa de Ahorro', total: 'TOTAL',
    fixedTotal: 'Total Gastos Fijos', variableTotal: 'Total Gastos Variables',
    totalDebt: 'Deuda Total', totalExpenses: 'Total Gastos',
    annualSavings: 'Ahorro Anual Proyectado',
    housingRatio: 'Ratio Vivienda', debtRatio: 'Ratio Deuda/Ingreso',
    month: 'Mes', expenses: 'Gastos', net: 'Neto',
    cumSavings: 'Ahorro Acumulado', annual: 'Anual',
    generatedBy: 'Generado por FinMaple \u2022 finmaple.com',
  },
  en: {
    dashboard: 'My Budget', subtitle: 'Personal Budget',
    income: 'INCOME', fixedExpenses: 'FIXED EXPENSES',
    variableExpenses: 'VARIABLE EXPENSES', debtPlan: 'DEBTS',
    projection: '12-Month Projection', summary: 'SUMMARY',
    monthlyIncome: 'Monthly Income', monthlyExpenses: 'Monthly Expenses',
    available: 'Available', healthScore: 'Financial Health',
    savingsRate: 'Savings Rate', total: 'TOTAL',
    fixedTotal: 'Total Fixed Expenses', variableTotal: 'Total Variable Expenses',
    totalDebt: 'Total Debt', totalExpenses: 'Total Expenses',
    annualSavings: 'Projected Annual Savings',
    housingRatio: 'Housing Ratio', debtRatio: 'Debt-to-Income',
    month: 'Month', expenses: 'Expenses', net: 'Net',
    cumSavings: 'Cumulative Savings', annual: 'Annual',
    generatedBy: 'Generated by FinMaple \u2022 finmaple.com',
  },
  fr: {
    dashboard: 'Mon Budget', subtitle: 'Budget Personnel',
    income: 'REVENUS', fixedExpenses: 'D\u00c9PENSES FIXES',
    variableExpenses: 'D\u00c9PENSES VARIABLES', debtPlan: 'DETTES',
    projection: 'Projection 12 Mois', summary: 'R\u00c9SUM\u00c9',
    monthlyIncome: 'Revenu Mensuel', monthlyExpenses: 'D\u00e9penses Mensuelles',
    available: 'Disponible', healthScore: 'Sant\u00e9 Financi\u00e8re',
    savingsRate: "Taux d'\u00c9pargne", total: 'TOTAL',
    fixedTotal: 'Total D\u00e9penses Fixes', variableTotal: 'Total D\u00e9penses Variables',
    totalDebt: 'Dette Totale', totalExpenses: 'Total D\u00e9penses',
    annualSavings: '\u00c9pargne Annuelle Projet\u00e9e',
    housingRatio: 'Ratio Logement', debtRatio: 'Ratio Dette/Revenu',
    month: 'Mois', expenses: 'D\u00e9penses', net: 'Net',
    cumSavings: '\u00c9pargne Cumul\u00e9e', annual: 'Annuel',
    generatedBy: 'G\u00e9n\u00e9r\u00e9 par FinMaple \u2022 finmaple.com',
  },
};

const MONTHS = {
  es: ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'],
  en: ['January','February','March','April','May','June','July','August','September','October','November','December'],
  fr: ['Janvier','F\u00e9vrier','Mars','Avril','Mai','Juin','Juillet','Ao\u00fbt','Septembre','Octobre','Novembre','D\u00e9cembre'],
};

/* ─── Main export ────────────────────────────────────────────────── */

export async function exportExcel(state, derived, lng = 'es') {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  wb.creator = 'FinMaple';
  wb.created = new Date();

  const L = L_ALL[lng] || L_ALL.es;
  const months = MONTHS[lng] || MONTHS.es;
  const now = new Date();

  /* ═══ Sheet 1 — Dashboard ══════════════════════════════════════════ */
  const ws = wb.addWorksheet(L.dashboard, {
    properties: { tabColor: { argb: P.navy } },
    views: [{ showGridLines: false }],
  });

  ws.columns = [
    { width: 1.5 },  // A spacer
    { width: 20 },   // B
    { width: 14 },   // C
    { width: 20 },   // D
    { width: 14 },   // E
    { width: 1.5 },  // F spacer
  ];

  let r = 1;

  /* ─── Brand header ──────────────────────── */
  fillRange(ws, r, 2, r, 5, P.navy);
  ws.mergeCells(r, 2, r, 5);
  const brand = ws.getCell(r, 2);
  brand.value = 'FinMaple';
  brand.font = { name: 'Calibri', size: 22, bold: true, color: { argb: P.white } };
  brand.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(r).height = 44;
  r++;

  fillRange(ws, r, 2, r, 5, P.navyPastel);
  ws.mergeCells(r, 2, r, 5);
  const sub = ws.getCell(r, 2);
  sub.value = `${L.subtitle}  \u2022  ${months[now.getMonth()]} ${now.getFullYear()}`;
  sub.font = { name: 'Calibri', size: 11, color: { argb: P.navy } };
  sub.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(r).height = 26;
  r++;

  ws.getRow(r).height = 10;
  r++;

  /* ─── Metric cards (2 x 2) ─────────────── */
  const healthScore = calculateHealthScore({
    savingsRate: derived.savingsRate,
    housingRatio: derived.housingRatio,
    debtToIncomeRatio: derived.debtToIncomeRatio,
    emergencyFundMonths: derived.currentEmergencyMonths || 0,
    incomeSourceCount: state.incomeSources.length,
  });

  const cards = [
    [
      { label: L.monthlyIncome, value: fmtCAD(derived.monthlyIncome), accent: P.emerald, pastel: P.emeraldPastel },
      { label: L.monthlyExpenses, value: fmtCAD(derived.monthlyTotalExpenses), accent: P.maple, pastel: P.maplePastel },
    ],
    [
      { label: L.available, value: fmtCAD(derived.monthlyNet), accent: derived.monthlyNet >= 0 ? P.emerald : P.maple, pastel: derived.monthlyNet >= 0 ? P.emeraldPastel : P.maplePastel },
      { label: L.healthScore, value: `${healthScore} / 100`, accent: P.navy, pastel: P.navyPastel },
    ],
  ];

  for (let pair = 0; pair < cards.length; pair++) {
    const [left, right] = cards[pair];

    // Label row
    fillRange(ws, r, 2, r, 3, left.pastel);
    fillRange(ws, r, 4, r, 5, right.pastel);
    ws.mergeCells(r, 2, r, 3);
    ws.mergeCells(r, 4, r, 5);
    ws.getCell(r, 2).value = left.label;
    ws.getCell(r, 2).font = { name: 'Calibri', size: 9, color: { argb: left.accent } };
    ws.getCell(r, 2).alignment = { vertical: 'bottom', horizontal: 'left', indent: 1 };
    ws.getCell(r, 4).value = right.label;
    ws.getCell(r, 4).font = { name: 'Calibri', size: 9, color: { argb: right.accent } };
    ws.getCell(r, 4).alignment = { vertical: 'bottom', horizontal: 'left', indent: 1 };
    ws.getRow(r).height = 18;
    r++;

    // Value row
    fillRange(ws, r, 2, r, 3, left.pastel);
    fillRange(ws, r, 4, r, 5, right.pastel);
    ws.mergeCells(r, 2, r, 3);
    ws.mergeCells(r, 4, r, 5);
    ws.getCell(r, 2).value = left.value;
    ws.getCell(r, 2).font = { name: 'Calibri', size: 20, bold: true, color: { argb: left.accent } };
    ws.getCell(r, 2).alignment = { vertical: 'top', horizontal: 'left', indent: 1 };
    ws.getCell(r, 4).value = right.value;
    ws.getCell(r, 4).font = { name: 'Calibri', size: 20, bold: true, color: { argb: right.accent } };
    ws.getCell(r, 4).alignment = { vertical: 'top', horizontal: 'left', indent: 1 };
    ws.getRow(r).height = 32;
    r++;

    if (pair === 0) { ws.getRow(r).height = 4; r++; }
  }

  ws.getRow(r).height = 15;
  r++;

  /* ─── Reusable section builder ──────────── */
  function addSection({ title, accent, pastel, tint, items, totalLabel, totalAmount }) {
    // Section header bar
    fillRange(ws, r, 2, r, 5, pastel);
    ws.mergeCells(r, 2, r, 5);
    ws.getCell(r, 2).value = title;
    ws.getCell(r, 2).font = { name: 'Calibri', size: 12, bold: true, color: { argb: accent } };
    ws.getCell(r, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    for (let c = 2; c <= 5; c++)
      ws.getCell(r, c).border = { bottom: { style: 'medium', color: { argb: accent } } };
    ws.getRow(r).height = 30;
    r++;

    // Data rows
    let alt = false;
    for (const item of items) {
      const rowBg = item.isCategory ? tint : (alt ? tint : P.white);
      fillRange(ws, r, 2, r, 5, rowBg);
      ws.mergeCells(r, 2, r, 4);

      ws.getCell(r, 2).value = item.name;
      ws.getCell(r, 2).font = item.isCategory
        ? { name: 'Calibri', size: 10, bold: true, color: { argb: accent } }
        : { name: 'Calibri', size: 10, color: { argb: P.textDark } };
      ws.getCell(r, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: item.isCategory ? 1 : 2 };

      ws.getCell(r, 5).value = item.amount;
      ws.getCell(r, 5).numFmt = currFmt;
      ws.getCell(r, 5).font = item.isCategory
        ? { name: 'Calibri', size: 10, bold: true, color: { argb: accent } }
        : { name: 'Calibri', size: 10, color: { argb: P.textDark } };
      ws.getCell(r, 5).alignment = { vertical: 'middle', horizontal: 'right' };

      for (let c = 2; c <= 5; c++)
        ws.getCell(r, c).border = { bottom: { style: 'thin', color: { argb: item.isCategory ? P.border : P.borderLight } } };

      ws.getRow(r).height = item.isCategory ? 22 : 20;
      if (!item.isCategory) alt = !alt;
      r++;
    }

    // Total row
    fillRange(ws, r, 2, r, 5, pastel);
    ws.mergeCells(r, 2, r, 4);
    ws.getCell(r, 2).value = totalLabel;
    ws.getCell(r, 2).font = { name: 'Calibri', size: 11, bold: true, color: { argb: accent } };
    ws.getCell(r, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    ws.getCell(r, 5).value = totalAmount;
    ws.getCell(r, 5).numFmt = currFmt;
    ws.getCell(r, 5).font = { name: 'Calibri', size: 11, bold: true, color: { argb: accent } };
    ws.getCell(r, 5).alignment = { vertical: 'middle', horizontal: 'right' };
    for (let c = 2; c <= 5; c++)
      ws.getCell(r, c).border = {
        top: { style: 'thin', color: { argb: accent } },
        bottom: { style: 'thin', color: { argb: accent } },
      };
    ws.getRow(r).height = 26;
    r++;

    ws.getRow(r).height = 12;
    r++;
  }

  /* ─── Income ────────────────────────────── */
  if (state.incomeSources.length > 0) {
    addSection({
      title: L.income, accent: P.emerald, pastel: P.emeraldPastel, tint: P.emeraldTint,
      items: state.incomeSources.map(s => ({ name: s.name, amount: toMonthly(s.amount, s.frequency) })),
      totalLabel: L.total, totalAmount: derived.monthlyIncome,
    });
  }

  /* ─── Fixed Expenses ────────────────────── */
  if (state.fixedExpenses.length > 0) {
    const grouped = {};
    for (const e of state.fixedExpenses) {
      const cat = e.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(e);
    }
    const items = [];
    for (const [cat, list] of Object.entries(grouped)) {
      items.push({ name: cat, amount: list.reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0), isCategory: true });
      for (const e of list) items.push({ name: e.name, amount: toMonthly(e.amount, e.frequency) });
    }
    addSection({
      title: L.fixedExpenses, accent: P.navy, pastel: P.navyPastel, tint: P.navyTint,
      items, totalLabel: L.fixedTotal, totalAmount: derived.monthlyFixedExpenses,
    });
  }

  /* ─── Variable Expenses ─────────────────── */
  if (state.variableExpenses.length > 0) {
    const grouped = {};
    for (const e of state.variableExpenses) {
      const cat = e.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(e);
    }
    const items = [];
    for (const [cat, list] of Object.entries(grouped)) {
      items.push({ name: cat, amount: list.reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0), isCategory: true });
      for (const e of list) items.push({ name: e.name, amount: toMonthly(e.amount, e.frequency) });
    }
    addSection({
      title: L.variableExpenses, accent: P.maple, pastel: P.maplePastel, tint: P.mapleTint,
      items, totalLabel: L.variableTotal, totalAmount: derived.monthlyVariableExpenses,
    });
  }

  /* ─── Debt (conditional) ────────────────── */
  if (state.goals.hasDebt && state.debts.length > 0) {
    const totalDebt = state.debts.reduce((s, d) => s + (d.balance || 0), 0);
    addSection({
      title: L.debtPlan, accent: P.gold, pastel: P.goldPastel, tint: P.goldTint,
      items: state.debts.map(d => ({ name: `${d.creditor} (${d.rate}%)`, amount: d.balance })),
      totalLabel: L.totalDebt, totalAmount: totalDebt,
    });
  }

  /* ─── Summary ───────────────────────────── */
  fillRange(ws, r, 2, r, 5, P.navy);
  ws.mergeCells(r, 2, r, 5);
  ws.getCell(r, 2).value = L.summary;
  ws.getCell(r, 2).font = { name: 'Calibri', size: 12, bold: true, color: { argb: P.white } };
  ws.getCell(r, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(r).height = 30;
  r++;

  const summaryRows = [
    [L.monthlyIncome, derived.monthlyIncome, true, P.emerald],
    [L.totalExpenses, derived.monthlyTotalExpenses, true, P.maple],
    [L.available, derived.monthlyNet, true, derived.monthlyNet >= 0 ? P.emerald : P.maple],
    [L.savingsRate, `${derived.savingsRate.toFixed(1)}%`, false, P.navy],
    [L.housingRatio, `${derived.housingRatio.toFixed(1)}%`, false, derived.housingRatio > 35 ? P.maple : P.navy],
    [L.annualSavings, derived.monthlyNet * 12, true, derived.monthlyNet >= 0 ? P.emerald : P.maple],
  ];

  let sAlt = false;
  for (const [label, value, isCurr, color] of summaryRows) {
    fillRange(ws, r, 2, r, 5, sAlt ? P.navyTint : P.white);
    ws.mergeCells(r, 2, r, 4);
    ws.getCell(r, 2).value = label;
    ws.getCell(r, 2).font = { name: 'Calibri', size: 10, bold: true, color: { argb: color } };
    ws.getCell(r, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    ws.getCell(r, 5).value = value;
    if (isCurr) ws.getCell(r, 5).numFmt = currFmt;
    ws.getCell(r, 5).font = { name: 'Calibri', size: 11, bold: true, color: { argb: color } };
    ws.getCell(r, 5).alignment = { vertical: 'middle', horizontal: 'right' };
    for (let c = 2; c <= 5; c++)
      ws.getCell(r, c).border = { bottom: { style: 'thin', color: { argb: P.borderLight } } };
    ws.getRow(r).height = 24;
    r++;
    sAlt = !sAlt;
  }

  ws.getRow(r).height = 10;
  r++;

  fillRange(ws, r, 2, r, 5, P.white);
  ws.mergeCells(r, 2, r, 5);
  ws.getCell(r, 2).value = L.generatedBy;
  ws.getCell(r, 2).font = { name: 'Calibri', size: 9, italic: true, color: { argb: P.textMuted } };
  ws.getCell(r, 2).alignment = { vertical: 'middle', horizontal: 'center' };

  /* ═══ Sheet 2 — 12-Month Projection ════════════════════════════════ */
  const wsP = wb.addWorksheet(L.projection, {
    properties: { tabColor: { argb: P.teal } },
    views: [{ showGridLines: false }],
  });

  wsP.columns = [
    { width: 1.5 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 18 }, { width: 1.5 },
  ];

  let rp = 1;

  // Header
  fillRange(wsP, rp, 2, rp, 6, P.navy);
  wsP.mergeCells(rp, 2, rp, 6);
  wsP.getCell(rp, 2).value = 'FinMaple';
  wsP.getCell(rp, 2).font = { name: 'Calibri', size: 22, bold: true, color: { argb: P.white } };
  wsP.getCell(rp, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  wsP.getRow(rp).height = 44;
  rp++;

  fillRange(wsP, rp, 2, rp, 6, P.tealPastel);
  wsP.mergeCells(rp, 2, rp, 6);
  wsP.getCell(rp, 2).value = L.projection;
  wsP.getCell(rp, 2).font = { name: 'Calibri', size: 12, bold: true, color: { argb: P.teal } };
  wsP.getCell(rp, 2).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  wsP.getRow(rp).height = 28;
  rp++;

  wsP.getRow(rp).height = 8;
  rp++;

  // Column headers
  const hdr = [L.month, L.monthlyIncome, L.expenses, L.net, L.cumSavings];
  for (let c = 0; c < 5; c++) {
    const cell = wsP.getCell(rp, c + 2);
    cell.value = hdr[c];
    cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: P.white } };
    cell.fill = bg(P.teal);
    cell.alignment = { vertical: 'middle', horizontal: c === 0 ? 'left' : 'center' };
    if (c === 0) cell.alignment.indent = 1;
  }
  wsP.getRow(rp).height = 26;
  rp++;

  // Monthly rows
  const startMonth = now.getMonth();
  let cumulative = 0;
  const mNet = derived.monthlyNet;

  for (let i = 0; i < 12; i++) {
    const monthIdx = (startMonth + i) % 12;
    cumulative += mNet;
    const rowBg = i % 2 === 0 ? P.tealTint : P.white;
    const vals = [months[monthIdx], derived.monthlyIncome, derived.monthlyTotalExpenses, mNet, cumulative];

    for (let c = 0; c < 5; c++) {
      const cell = wsP.getCell(rp, c + 2);
      cell.value = vals[c];
      cell.fill = bg(rowBg);
      cell.alignment = { vertical: 'middle', horizontal: c === 0 ? 'left' : 'right' };
      if (c === 0) {
        cell.font = { name: 'Calibri', size: 10, color: { argb: P.textDark } };
        cell.alignment.indent = 1;
      } else {
        cell.numFmt = currFmt;
        const color = c === 3 ? (mNet >= 0 ? P.emerald : P.maple)
          : c === 4 ? (cumulative >= 0 ? P.emerald : P.maple)
          : P.textDark;
        cell.font = { name: 'Calibri', size: 10, bold: c === 3, color: { argb: color } };
      }
      cell.border = { bottom: { style: 'thin', color: { argb: P.borderLight } } };
    }
    wsP.getRow(rp).height = 22;
    rp++;
  }

  // Annual total
  const annualVals = [L.annual, derived.monthlyIncome * 12, derived.monthlyTotalExpenses * 12, mNet * 12, cumulative];
  for (let c = 0; c < 5; c++) {
    const cell = wsP.getCell(rp, c + 2);
    cell.value = annualVals[c];
    cell.fill = bg(P.tealPastel);
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: P.teal } };
    cell.alignment = { vertical: 'middle', horizontal: c === 0 ? 'left' : 'right' };
    if (c === 0) cell.alignment.indent = 1;
    if (c > 0) cell.numFmt = currFmt;
    cell.border = { top: { style: 'thin', color: { argb: P.teal } }, bottom: { style: 'thin', color: { argb: P.teal } } };
  }
  wsP.getRow(rp).height = 28;
  rp += 2;

  wsP.mergeCells(rp, 2, rp, 6);
  wsP.getCell(rp, 2).value = L.generatedBy;
  wsP.getCell(rp, 2).font = { name: 'Calibri', size: 9, italic: true, color: { argb: P.textMuted } };
  wsP.getCell(rp, 2).alignment = { vertical: 'middle', horizontal: 'center' };

  /* ═══ Download ═════════════════════════════════════════════════════ */
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `FinMaple-Budget-${now.toISOString().slice(0, 10)}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
